/**
 * Solicitation detail (reference-only).
 *
 * Shows the solicitation fields, a "Check match (suggestion only)" button that
 * fetches and shows the NON-BINDING match suggestion, and a "Record bid decision"
 * Select (bid / no-bid) that records the human's choice.
 *
 * LEGAL / SAFETY (CRITICAL): the match is a non-binding suggestion — BEAM never
 * bids. "Record bid decision" only records a HUMAN's bid/no-bid choice; it does
 * not submit a bid anywhere. NEVER imports @smartbox/* (mobile invariant 1).
 * No raw style values.
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSolicitation,
  matchSolicitation,
  recordBidDecision,
  type SolicitationMatchRow,
} from '../../../src/api/gov';
import {
  Screen,
  Text,
  Badge,
  Card,
  Button,
  Select,
} from '../../../src/components/ui';
import type { StatusKey } from '../../../src/theme';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize, formatDateTime } from '../../../src/lib/format';

const BID_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'bid', label: 'Bid' },
  { value: 'no_bid', label: 'No-bid' },
];

function solicitationBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'open':
    case 'active':
    case 'posted':
      return { key: 'sent', label: humanize(status) };
    case 'awarded':
      return { key: 'paid', label: 'Awarded' };
    case 'closed':
    case 'cancelled':
    case 'archived':
      return { key: 'draft', label: humanize(status) };
    default:
      return { key: 'inProgress', label: humanize(status) || 'Open' };
  }
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.cardRow}>
      <Text variant="small" color="secondary">
        {label}
      </Text>
      <Text variant="data" style={styles.rowValue}>
        {value}
      </Text>
    </View>
  );
}

function MatchView({ match }: { match: SolicitationMatchRow }) {
  return (
    <Card>
      <View style={styles.cardHeader}>
        <Text variant="bodyStrong" style={styles.flex1}>
          Match suggestion
        </Text>
        {typeof match.score === 'number' ? (
          <Badge status="sent" label={`${Math.round(match.score)}%`} />
        ) : null}
      </View>
      <Text variant="small" color="secondary">
        This is a non-binding suggestion to help you decide. It is not a bid and does
        not commit you to anything.
      </Text>
      {match.recommendation ? (
        <Text variant="body">{match.recommendation}</Text>
      ) : null}
      {match.reasons && match.reasons.length > 0 ? (
        <View style={styles.bulletGroup}>
          <Text variant="label" color="secondary">
            Why it might fit
          </Text>
          {match.reasons.map((r, i) => (
            <Text key={i} variant="small" color="secondary">
              • {r}
            </Text>
          ))}
        </View>
      ) : null}
      {match.gaps && match.gaps.length > 0 ? (
        <View style={styles.bulletGroup}>
          <Text variant="label" color="secondary">
            Gaps to consider
          </Text>
          {match.gaps.map((g, i) => (
            <Text key={i} variant="small" color="secondary">
              • {g}
            </Text>
          ))}
        </View>
      ) : null}
    </Card>
  );
}

export default function SolicitationDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [decision, setDecision] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const solQuery = useQuery({
    queryKey: ['gov-solicitation', id],
    queryFn: () => getSolicitation(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const matchMutation = useMutation({
    mutationFn: () => matchSolicitation(id!),
    onError: (err) => setActionError(extractErrorMessage(err, 'Could not load the match suggestion.')),
    onSuccess: () => setActionError(null),
  });
  const bidMutation = useMutation({
    mutationFn: (d: string) => recordBidDecision(id!, d),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gov-solicitation', id] });
      void queryClient.invalidateQueries({ queryKey: ['gov-solicitations'] });
      setActionError(null);
    },
    onError: (err) => setActionError(extractErrorMessage(err, 'Could not record the bid decision.')),
  });

  if (!id) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="body" color="danger">
            No solicitation ID provided.
          </Text>
        </View>
      </Screen>
    );
  }

  if (solQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading solicitation" />
          <Text variant="body" color="secondary">
            Loading…
          </Text>
        </View>
      </Screen>
    );
  }

  if (solQuery.isError) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(solQuery.error, 'Failed to load solicitation.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => solQuery.refetch()} />
          </Card>
        </View>
      </Screen>
    );
  }

  const sol = solQuery.data;
  if (!sol) return null;

  const b = solicitationBadge(sol.status);

  return (
    <Screen scroll>
      <View style={styles.statusRow}>
        <Text variant="title" style={styles.flex1}>
          {sol.title}
        </Text>
        <Badge status={b.key} label={b.label} />
      </View>

      {/* Fields */}
      <Card>
        {sol.solicitation_number ? <Row label="Number" value={sol.solicitation_number} /> : null}
        {sol.issuing_agency ? <Row label="Agency" value={sol.issuing_agency} /> : null}
        {sol.naics_code ? <Row label="NAICS" value={sol.naics_code} /> : null}
        {sol.set_aside_type && sol.set_aside_type !== 'none' ? <Row label="Set-aside" value={humanize(sol.set_aside_type)} /> : null}
        <Row label="Status" value={humanize(sol.status)} />
        {sol.posted_date ? <Row label="Posted" value={formatDateTime(sol.posted_date)} /> : null}
        {sol.response_due ? <Row label="Response due" value={formatDateTime(sol.response_due)} /> : null}
        {sol.bid_decision ? <Row label="Bid decision" value={humanize(sol.bid_decision)} /> : null}
      </Card>

      {sol.source_url ? (
        <Card>
          <Text variant="label" color="secondary">
            Source
          </Text>
          <Text variant="body">{sol.source_url}</Text>
        </Card>
      ) : null}

      {/* Match — non-binding suggestion */}
      <View style={styles.section}>
        <Button
          title="Check match (suggestion only)"
          variant="secondary"
          loading={matchMutation.isPending}
          disabled={matchMutation.isPending}
          onPress={() => matchMutation.mutate()}
          accessibilityLabel="Get a non-binding match suggestion for this solicitation"
        />
        {matchMutation.data ? <MatchView match={matchMutation.data} /> : null}
      </View>

      {/* Bid decision — records a HUMAN choice */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Record bid decision
        </Text>
        <Card>
          <Text variant="small" color="secondary">
            Record your own bid / no-bid decision. This only logs your choice — BEAM
            does not submit a bid for you.
          </Text>
          <Select options={BID_OPTIONS} value={decision ?? sol.bid_decision} onChange={setDecision} />
          <Button
            title="Record bid decision"
            variant="secondary"
            loading={bidMutation.isPending}
            disabled={bidMutation.isPending || !decision || decision === sol.bid_decision}
            onPress={() => decision && bidMutation.mutate(decision)}
            accessibilityLabel="Record your bid or no-bid decision"
          />
        </Card>
      </View>

      {actionError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {actionError}
          </Text>
        </Card>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  flex1: { flex: 1 },
  section: { gap: theme.spacing.sm },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing.md,
  },
  rowValue: { flex: 1, textAlign: 'right' },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  bulletGroup: { gap: theme.spacing.xs },
});
